import type { ReactNode } from 'react';
import { ApiError } from '../api';

export function PageTitle({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
        {subtitle && <div className="mt-0.5 text-sm text-muted">{subtitle}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{actions}</div>
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof ApiError ? `${error.body.message} (${error.body.code})` : error instanceof Error ? error.message : String(error);
  return <div className="mb-3 rounded-xl border border-danger/30 bg-danger-100 px-4 py-2.5 text-sm text-danger">{msg}</div>;
}

export function Spinner() {
  return (
    <div className="flex items-center gap-2 py-6 text-sm text-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" /> Henter…
    </div>
  );
}

export function Badge({ children, tone = 'zinc' }: { children: ReactNode; tone?: 'zinc' | 'green' | 'red' | 'amber' | 'blue' }) {
  const tones = {
    zinc: 'bg-canvas text-muted',
    green: 'bg-success-100 text-success',
    red: 'bg-danger-100 text-danger',
    amber: 'bg-warning-100 text-amber-700',
    blue: 'bg-primary-100 text-primary',
  } as const;
  return <span className={`badge ${tones[tone]}`}>{children}</span>;
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/30 p-4 backdrop-blur-[2px]" onClick={onClose}>
      <div className="card w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">{title}</h2>
          <button className="rounded-lg px-2 text-muted hover:bg-canvas hover:text-ink" onClick={onClose} aria-label="Luk">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** A figure with an optional change versus the previous period, like the tiles on the reference dashboard. */
export function StatTile({ label, value, delta, deltaLabel, tone, to }: { label: string; value: ReactNode; delta?: number | null; deltaLabel?: string; tone?: 'up-good' | 'up-bad'; to?: ReactNode }) {
  const good = delta === undefined || delta === null ? null : tone === 'up-bad' ? delta <= 0 : delta >= 0;
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {delta !== undefined && delta !== null && (
        <div className={`mt-1 flex items-center gap-1 text-xs font-medium ${good ? 'text-success' : 'text-danger'}`}>
          <span>{delta > 0 ? '▲' : delta < 0 ? '▼' : '•'}</span>
          <span>{delta > 0 ? '+' : ''}{delta}{deltaLabel ? ` ${deltaLabel}` : ''}</span>
        </div>
      )}
      {to && <div className="mt-2 text-xs">{to}</div>}
    </div>
  );
}

/** Smooth line chart with a soft fill, two series at most, no dependency. */
export function LineChart({ series, labels, height = 220 }: { series: { name: string; values: number[]; color: string; fill?: string }[]; labels: string[]; height?: number }) {
  const w = 600;
  const h = height;
  const pad = { l: 36, r: 12, t: 12, b: 24 };
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const n = Math.max(1, labels.length - 1);
  const x = (i: number) => pad.l + (i / n) * (w - pad.l - pad.r);
  const y = (v: number) => pad.t + (1 - v / max) * (h - pad.t - pad.b);
  const path = (vals: number[]) => {
    if (!vals.length) return '';
    let d = `M ${x(0)} ${y(vals[0]!)}`;
    for (let i = 1; i < vals.length; i++) {
      const cx = (x(i - 1) + x(i)) / 2;
      d += ` C ${cx} ${y(vals[i - 1]!)}, ${cx} ${y(vals[i]!)}, ${x(i)} ${y(vals[i]!)}`;
    }
    return d;
  };
  const ticks = [0, 0.5, 1].map((f) => Math.round(max * f));
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-auto w-full" role="img">
      {ticks.map((t) => (
        <g key={t}>
          <line x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} stroke="#e6e8ec" strokeDasharray="3 4" />
          <text x={pad.l - 6} y={y(t) + 4} fontSize="10" textAnchor="end" fill="#737f8b">{t}</text>
        </g>
      ))}
      {series.map((s) => (
        <g key={s.name}>
          {s.fill && <path d={`${path(s.values)} L ${x(s.values.length - 1)} ${y(0)} L ${x(0)} ${y(0)} Z`} fill={s.fill} opacity="0.35" />}
          <path d={path(s.values)} fill="none" stroke={s.color} strokeWidth="2.2" strokeLinejoin="round" />
          {s.values.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r="2.6" fill="#fff" stroke={s.color} strokeWidth="1.8" />)}
        </g>
      ))}
      {labels.map((l, i) => (labels.length <= 8 || i % Math.ceil(labels.length / 8) === 0) && <text key={i} x={x(i)} y={h - 6} fontSize="10" textAnchor="middle" fill="#737f8b">{l}</text>)}
    </svg>
  );
}

/** Grouped bars, two series at most. */
export function BarChart({ series, labels, height = 200 }: { series: { name: string; values: number[]; color: string }[]; labels: string[]; height?: number }) {
  const w = 600;
  const h = height;
  const pad = { l: 36, r: 12, t: 12, b: 24 };
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const slot = (w - pad.l - pad.r) / Math.max(1, labels.length);
  const bw = Math.min(18, (slot * 0.6) / Math.max(1, series.length));
  const y = (v: number) => pad.t + (1 - v / max) * (h - pad.t - pad.b);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-auto w-full" role="img">
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line x1={pad.l} x2={w - pad.r} y1={y(max * f)} y2={y(max * f)} stroke="#e6e8ec" strokeDasharray="3 4" />
          <text x={pad.l - 6} y={y(max * f) + 4} fontSize="10" textAnchor="end" fill="#737f8b">{Math.round(max * f)}</text>
        </g>
      ))}
      {labels.map((l, i) => (
        <g key={i}>
          {series.map((s, k) => {
            const v = s.values[i] ?? 0;
            const cx = pad.l + slot * i + slot / 2 + (k - (series.length - 1) / 2) * (bw + 3);
            return <rect key={s.name} x={cx - bw / 2} y={y(v)} width={bw} height={Math.max(0, y(0) - y(v))} rx="3" fill={s.color} />;
          })}
          {(labels.length <= 10 || i % Math.ceil(labels.length / 10) === 0) && <text x={pad.l + slot * i + slot / 2} y={h - 6} fontSize="10" textAnchor="middle" fill="#737f8b">{l}</text>}
        </g>
      ))}
    </svg>
  );
}

/** A ring with a percentage, like the visitor donuts on the reference. */
export function Ring({ percent, color = '#1f3bb3', size = 56, label }: { percent: number; color?: string; size?: number; label?: ReactNode }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, percent));
  return (
    <div className="flex items-center gap-3">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#e6e8ec" strokeWidth="7" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="7" fill="none" strokeDasharray={`${(c * p) / 100} ${c}`} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      {label}
    </div>
  );
}

export const fmtTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('da-DK', { timeZone: 'Europe/Copenhagen', dateStyle: 'short', timeStyle: 'short' }) : '–';
